import { registerPlugin } from "@capacitor/core";
import { invoke } from "@tauri-apps/api/core";

const WEB_BASE_URL = (process.env.REACT_APP_WEB_BASE_URL || "https://www.quicklearning-cmr.com").replace(/\/$/, "");
const PLATFORM = process.env.REACT_APP_PLATFORM || "web";
const NativeDownloader = registerPlugin("NativeDownloader");

const ALLOWED_HOSTS = new Set([
  "www.quicklearning-cmr.com",
  "quicklearning-cmr.com",
  "res.cloudinary.com",
]);
const AUTHENTICATED_HOSTS = new Set(["www.quicklearning-cmr.com", "quicklearning-cmr.com"]);

export class DownloadError extends Error {
  constructor(code, message, status = null) {
    super(message);
    this.name = "DownloadError";
    this.code = code;
    this.status = status;
  }
}

export function normalizeDownloadUrl(value) {
  if (!value) throw new DownloadError("invalid_url", "Adresse du fichier manquante.");
  let parsed;
  try {
    parsed = new URL(value, `${WEB_BASE_URL}/`);
  } catch {
    throw new DownloadError("invalid_url", "Adresse du fichier invalide.");
  }
  if (parsed.protocol !== "https:" || !ALLOWED_HOSTS.has(parsed.hostname.toLowerCase())) {
    throw new DownloadError("domain_refused", "Ce domaine de téléchargement n’est pas autorisé.");
  }
  return parsed.toString();
}

export function isAuthenticatedDownloadUrl(value) {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:" && AUTHENTICATED_HOSTS.has(parsed.hostname.toLowerCase());
  } catch {
    return false;
  }
}

export function sanitizeFilename(value, mimeType = "") {
  const fallback = mimeType === "application/pdf" ? "document.pdf" : "telechargement";
  let name = Array.from(String(value || fallback), character => (
    character.charCodeAt(0) < 32 ? "_" : character
  )).join("")
    .replace(/\\/g, "/")
    .split("/").pop()
    .replace(/\.\.+/g, ".")
    .replace(/[<>:"/\\|?*]/g, "_")
    .replace(/[. ]+$/g, "")
    .trim();
  if (!name) name = fallback;
  const extension = name.lastIndexOf(".");
  if (name.length > 180) {
    name = extension > 0
      ? `${name.slice(0, Math.max(1, 180 - (name.length - extension)))}${name.slice(extension)}`
      : name.slice(0, 180);
  }
  return name;
}

function filenameFromDisposition(header) {
  if (!header) return "";
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8) {
    try { return decodeURIComponent(utf8[1].replace(/^"|"$/g, "")); } catch {}
  }
  return header.match(/filename\s*=\s*"([^"]+)"/i)?.[1]
    || header.match(/filename\s*=\s*([^;]+)/i)?.[1]?.trim()
    || "";
}

function getAccessToken(authenticated) {
  if (!authenticated) return null;
  const token = localStorage.getItem("access_token");
  if (!token) throw new DownloadError("session_expired", "Votre session a expiré. Reconnectez-vous.", 401);
  return token;
}

function inferFilename(url, supplied, disposition, mimeType) {
  const pathName = (() => {
    try { return decodeURIComponent(new URL(url).pathname.split("/").pop() || ""); } catch { return ""; }
  })();
  return sanitizeFilename(supplied || filenameFromDisposition(disposition) || pathName, mimeType);
}

async function ensureValidResponse(response) {
  if (response.status === 401 || response.status === 403) {
    throw new DownloadError("session_expired", "Votre session a expiré. Reconnectez-vous.", response.status);
  }
  if (response.status === 404) throw new DownloadError("not_found", "Fichier introuvable.", 404);
  if (!response.ok) throw new DownloadError("http_error", `Téléchargement impossible (${response.status}).`, response.status);
  const type = (response.headers.get("content-type") || "").toLowerCase();
  if (type.includes("text/html")) throw new DownloadError("invalid_content", "Le serveur a renvoyé une page HTML au lieu du fichier.");
}

async function webDownload({ url, filename, mimeType, authenticated, data }) {
  let blob = data instanceof Blob ? data : null;
  let disposition = "";
  if (!blob) {
    const token = getAccessToken(authenticated);
    const response = await fetch(url, {
      method: "GET",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      redirect: "follow",
    });
    await ensureValidResponse(response);
    disposition = response.headers.get("content-disposition") || "";
    blob = await response.blob();
  }
  if (!blob.size) throw new DownloadError("empty_file", "Le fichier reçu est vide.");
  if ((blob.type || mimeType || "").toLowerCase().includes("text/html")) {
    throw new DownloadError("invalid_content", "Le serveur a renvoyé une page HTML au lieu du fichier.");
  }
  const finalName = inferFilename(url, filename, disposition, blob.type || mimeType);
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = finalName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
  return { cancelled: false, filename: finalName };
}

async function blobToBase64(blob) {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export async function downloadFile({
  url,
  filename,
  mimeType = "application/octet-stream",
  authenticated = false,
  openAfterDownload = false,
  data = null,
}) {
  const normalizedUrl = normalizeDownloadUrl(url);
  const safeName = inferFilename(normalizedUrl, filename, "", mimeType);
  const token = authenticated && isAuthenticatedDownloadUrl(normalizedUrl) ? getAccessToken(true) : null;

  if (PLATFORM === "windows") {
    return invoke("download_file", {
      request: {
        url: normalizedUrl,
        filename: safeName,
        mimeType,
        bearerToken: token,
        openAfterDownload,
        base64Data: data instanceof Blob ? await blobToBase64(data) : null,
      },
    });
  }

  if (PLATFORM === "android") {
    return NativeDownloader.download({
      url: normalizedUrl,
      filename: safeName,
      mimeType,
      bearerToken: token,
      openAfterDownload,
      base64Data: data instanceof Blob ? await blobToBase64(data) : null,
    });
  }

  return webDownload({ url: normalizedUrl, filename: safeName, mimeType, authenticated, data });
}

export async function openLastDownload() {
  if (PLATFORM === "windows") return invoke("open_last_download");
  if (PLATFORM === "android") return NativeDownloader.openLast();
}

export async function revealLastDownload() {
  if (PLATFORM === "windows") return invoke("reveal_last_download");
}

export async function shareLastDownload() {
  if (PLATFORM === "android") return NativeDownloader.shareLast();
}

export default downloadFile;
